// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { supabase } from '../../supabaseClient';
import type { Session } from '@supabase/supabase-js';
import type { Profile } from '../../types';
import type { EmailTemplate } from '../../types/admin';
import { AdminSearchBar } from './AdminSearchBar';
import { AdminLoadingSkeleton } from './AdminLoadingSkeleton';
import { AdminConfirmDialog } from './AdminConfirmDialog';
import { useAdminToast } from './useAdminToast';
import { RichTextEditor } from './RichTextEditor';

interface AdminEmailTemplatesProps {
  session: Session;
  profile: Profile | null;
}

const defaultFormState = {
  name: '',
  subject: '',
  preview_text: '',
  html_body: '<p>Hallo {{first_name}},</p>\n<p>wir haben Neuigkeiten für Sie!</p>\n<p>Herzliche Grüße<br/>Ihr LRDEnE Team</p>',
  text_body: '',
  tagsInput: '',
};

const slugify = (value: string) => {
  const slug = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80);
  return slug || `template-${Date.now()}`;
};

export const AdminEmailTemplates: React.FC<AdminEmailTemplatesProps> = ({ session, profile }) => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(defaultFormState);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<EmailTemplate | null>(null);
  const { showSuccess, showError } = useAdminToast();

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('email_templates')
        .select('*')
        .order('updated_at', { ascending: false });
      if (error) throw error;
      setTemplates(data || []);
    } catch (err: any) {
      console.error('Error loading email templates:', err);
      showError(err.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const filteredTemplates = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return templates;
    return templates.filter((template) => {
      const tags = (template.tags || []).join(' ').toLowerCase();
      return (
        template.name.toLowerCase().includes(query) ||
        (template.subject || '').toLowerCase().includes(query) ||
        (template.preview_text || '').toLowerCase().includes(query) ||
        tags.includes(query)
      );
    });
  }, [templates, searchQuery]);

  const resetForm = () => {
    setFormData(defaultFormState);
    setEditingTemplate(null);
  };

  const handleCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEdit = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      subject: template.subject,
      preview_text: template.preview_text || '',
      html_body: template.html_body || defaultFormState.html_body,
      text_body: template.text_body || '',
      tagsInput: (template.tags || []).join(', '),
    });
    setShowForm(true);
  };

  const parseTags = (input: string) =>
    input
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);

  const handleFormSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!formData.name.trim() || !formData.subject.trim()) {
      showError('Name and subject are required.');
      return;
    }

    const nameValue = formData.name.trim();
    const computedSlug = slugify(nameValue);
    const payload = {
      name: nameValue,
      slug: editingTemplate?.slug && editingTemplate.name?.trim() === nameValue
        ? editingTemplate.slug
        : computedSlug,
      subject: formData.subject.trim(),
      preview_text: formData.preview_text.trim() || null,
      html_body: formData.html_body || null,
      text_body: formData.text_body.trim() || null,
      tags: parseTags(formData.tagsInput),
    };

    try {
      if (editingTemplate) {
        const { error } = await supabase
          .from('email_templates')
          .update(payload)
          .eq('id', editingTemplate.id);
        if (error) throw error;
        showSuccess('Template updated successfully');
      } else {
        const { error } = await supabase
          .from('email_templates')
          .insert({
            ...payload,
            created_by: profile?.id || session?.user?.id || null,
          });
        if (error) throw error;
        showSuccess('Template created successfully');
      }
      setShowForm(false);
      resetForm();
      await loadTemplates();
    } catch (err: any) {
      console.error('Error saving template:', err);
      showError(err.message || 'Failed to save template');
    }
  };

  const handleDuplicate = async (template: EmailTemplate) => {
    try {
      const { error } = await supabase.from('email_templates').insert({
        name: `${template.name} Copy`,
        subject: template.subject,
        preview_text: template.preview_text,
        html_body: template.html_body,
        text_body: template.text_body,
        tags: template.tags || [],
        created_by: profile?.id || session?.user?.id || null,
      });
      if (error) throw error;
      showSuccess('Template duplicated');
      await loadTemplates();
    } catch (err: any) {
      console.error('Error duplicating template:', err);
      showError(err.message || 'Failed to duplicate template');
    }
  };

  const handleDelete = (template: EmailTemplate) => {
    setDeleteTarget(template);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const { error } = await supabase
        .from('email_templates')
        .delete()
        .eq('id', deleteTarget.id);
      if (error) throw error;
      showSuccess('Template deleted');
      await loadTemplates();
    } catch (err: any) {
      console.error('Error deleting template:', err);
      showError(err.message || 'Failed to delete template');
    } finally {
      setShowDeleteDialog(false);
      setDeleteTarget(null);
    }
  };

  const handleCopy = async (value: string | null | undefined, label: string, templateId?: string) => {
    if (!value) {
      showError(`No ${label.toLowerCase()} to copy.`);
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      showSuccess(`${label} copied to clipboard`);
      if (templateId) {
        const now = new Date().toISOString();
        setTemplates((prev) =>
          prev.map((tpl) => (tpl.id === templateId ? { ...tpl, last_used_at: now } : tpl))
        );
        await supabase
          .from('email_templates')
          .update({ last_used_at: now })
          .eq('id', templateId);
      }
    } catch (err) {
      console.error('Error copying text:', err);
      showError('Failed to copy to clipboard');
    }
  };

  const formatTimestamp = (value?: string | null) => {
    if (!value) return 'Never';
    try {
      return formatDistanceToNow(new Date(value), { addSuffix: true });
    } catch {
      return value;
    }
  };

  if (loading) {
    return <AdminLoadingSkeleton rows={8} columns={5} />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-black text-luna-text-light dark:text-luna-text-dark mb-1">
            Email Templates
          </h1>
          <p className="text-luna-text-light/70 dark:text-luna-text-dark/70">
            Create reusable campaigns and hand them over to the bulk email tool or AI assistant.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <button
            onClick={handleCreate}
            className="px-4 py-2.5 bg-luna-secondary text-white rounded-lg hover:opacity-90 min-h-[44px]"
          >
            <span className="material-symbols-outlined align-middle mr-2 text-base">add</span>
            New Template
          </button>
        </div>
      </div>

      <AdminSearchBar
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Search templates by name, subject, or tag..."
      />

      {showForm && (
        <div className="bg-white dark:bg-luna-primary border-2 border-luna-secondary/30 rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-luna-text-light dark:text-luna-text-dark">
                {editingTemplate ? 'Edit Template' : 'Create Template'}
              </h2>
            <p className="text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
              Use {"{{first_name}}"}, {"{{last_name}}"} and other merge tags supported by the bulk email function.
            </p>
            </div>
            <button
              onClick={() => {
                setShowForm(false);
                resetForm();
              }}
              className="p-2 rounded-lg hover:bg-luna-secondary/10"
              aria-label="Close form"
            >
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>

          <form className="space-y-4" onSubmit={handleFormSubmit}>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="template-name" className="block text-sm font-medium text-luna-text-light dark:text-luna-text-dark mb-1">
                  Template Name *
                </label>
                <input
                  id="template-name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-lg border border-luna-secondary/30 bg-white dark:bg-luna-primary"
                  required
                />
              </div>
              <div>
                <label htmlFor="template-subject" className="block text-sm font-medium text-luna-text-light dark:text-luna-text-dark mb-1">
                  Subject *
                </label>
                <input
                  id="template-subject"
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData((prev) => ({ ...prev, subject: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-lg border border-luna-secondary/30 bg-white dark:bg-luna-primary"
                  required
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="template-preview" className="block text-sm font-medium text-luna-text-light dark:text-luna-text-dark mb-1">
                  Preview Text
                </label>
                <input
                  id="template-preview"
                  type="text"
                  value={formData.preview_text}
                  onChange={(e) => setFormData((prev) => ({ ...prev, preview_text: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-lg border border-luna-secondary/30 bg-white dark:bg-luna-primary"
                  placeholder="Shown next to the subject in inboxes"
                />
              </div>
              <div>
                <label htmlFor="template-tags" className="block text-sm font-medium text-luna-text-light dark:text-luna-text-dark mb-1">
                  Tags
                </label>
                <input
                  id="template-tags"
                  type="text"
                  value={formData.tagsInput}
                  onChange={(e) => setFormData((prev) => ({ ...prev, tagsInput: e.target.value }))}
                  className="w-full px-4 py-2.5 rounded-lg border border-luna-secondary/30 bg-white dark:bg-luna-primary"
                  placeholder="e.g. onboarding, upgrade, churn-prevention"
                />
              </div>
            </div>

            <div>
              <label htmlFor="html-body" className="block text-sm font-medium text-luna-text-light dark:text-luna-text-dark mb-1">
                HTML Body
              </label>
              <RichTextEditor
                id="html-body"
                content={formData.html_body}
                onChange={(value) => setFormData((prev) => ({ ...prev, html_body: value }))}
              />
            </div>

            <div>
              <label htmlFor="text-body" className="block text-sm font-medium text-luna-text-light dark:text-luna-text-dark mb-1">
                Plain-Text Body (optional)
              </label>
              <textarea
                id="text-body"
                value={formData.text_body}
                onChange={(e) => setFormData((prev) => ({ ...prev, text_body: e.target.value }))}
                rows={4}
                className="w-full px-4 py-2.5 rounded-lg border border-luna-secondary/30 bg-white dark:bg-luna-primary"
                placeholder="Fallback text version for inboxes that block HTML"
              />
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:justify-between">
              <div className="text-xs text-luna-text-light/60 dark:text-luna-text-dark/60">
                Tip: Use merge tags like <code>{"{{first_name}}"}</code> or <code>{"{{plan_name}}"}</code>.
              </div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                  className="px-4 py-2.5 rounded-lg border border-luna-secondary/30 min-h-[44px]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2.5 rounded-lg bg-luna-secondary text-white hover:opacity-90 min-h-[44px]"
                >
                  {editingTemplate ? 'Save Changes' : 'Create Template'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white dark:bg-luna-primary border border-luna-secondary/20 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-luna-secondary/20">
            <thead className="bg-luna-secondary/10">
              <tr className="text-left text-sm font-semibold text-luna-text-light dark:text-luna-text-dark">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Subject</th>
                <th className="px-4 py-3 hidden lg:table-cell">Tags</th>
                <th className="px-4 py-3">Last Updated</th>
                <th className="px-4 py-3 hidden sm:table-cell">Last Used</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-luna-secondary/20 text-sm text-luna-text-light dark:text-luna-text-dark">
              {filteredTemplates.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-luna-text-light/60 dark:text-luna-text-dark/60">
                    No templates found. Create one to get started.
                  </td>
                </tr>
              )}
              {filteredTemplates.map((template) => (
                <tr key={template.id} className="hover:bg-luna-secondary/5">
                  <td className="px-4 py-3 font-semibold">
                    <div>{template.name}</div>
                    {template.preview_text && (
                      <div className="text-xs text-luna-text-light/60 dark:text-luna-text-dark/60">
                        {template.preview_text}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {template.subject}
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    {(template.tags || []).length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {template.tags!.map((tag) => (
                          <span
                            key={`${template.id}-${tag}`}
                            className="px-2 py-0.5 rounded-full bg-luna-secondary/15 text-xs font-medium"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-luna-text-light/50 dark:text-luna-text-dark/50">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {formatTimestamp(template.updated_at)}
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    {formatTimestamp(template.last_used_at)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2 justify-end">
                      <button
                        onClick={() => handleCopy(template.subject, 'Subject', template.id)}
                        className="p-2 rounded-lg border border-luna-secondary/30 hover:bg-luna-secondary/10"
                        title="Copy subject"
                      >
                        <span className="material-symbols-outlined text-base">content_copy</span>
                      </button>
                      <button
                        onClick={() => handleCopy(template.html_body, 'HTML body', template.id)}
                        className="p-2 rounded-lg border border-luna-secondary/30 hover:bg-luna-secondary/10"
                        title="Copy HTML body"
                      >
                        <span className="material-symbols-outlined text-base">code</span>
                      </button>
                      <button
                        onClick={() => handleDuplicate(template)}
                        className="p-2 rounded-lg border border-luna-secondary/30 hover:bg-luna-secondary/10"
                        title="Duplicate"
                      >
                        <span className="material-symbols-outlined text-base">content_copy</span>
                      </button>
                      <button
                        onClick={() => handleEdit(template)}
                        className="p-2 rounded-lg border border-luna-secondary/30 hover:bg-luna-secondary/10"
                        title="Edit"
                      >
                        <span className="material-symbols-outlined text-base">edit</span>
                      </button>
                      <button
                        onClick={() => handleDelete(template)}
                        className="p-2 rounded-lg border border-red-300 text-red-600 hover:bg-red-50"
                        title="Delete"
                      >
                        <span className="material-symbols-outlined text-base">delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AdminConfirmDialog
        isOpen={showDeleteDialog}
        title="Delete template"
        description={`Are you sure you want to delete "${deleteTarget?.name}"?`}
        confirmLabel="Delete"
        confirmColor="danger"
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteDialog(false)}
      />
    </div>
  );
};
